import { usePageMeta } from '../lib/usePageMeta'
import { Link } from 'react-router'
import {
  ArrowRight,
  Camera,
  CircleCheck,
  CircleHelp,
  Crop,
  Lock,
  MessageCircle,
  PlayCircle,
  RectangleVertical,
  ShoppingBag,
  Sparkles,
  Store,
  Wand2,
  Zap,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { LanguageToggle } from '../components/LanguageToggle'
import { LegalLinks } from '../components/LegalLinks'
import { LogoMark, Wordmark } from '../components/Logo'
import { useI18n } from '../i18n/context'
import type { MessageKey } from '../i18n/messages'

/** Shopper entry stays useful before an operator publishes a demo product. */
const SHOPPER_PATH = '/try'

const TRUST: { icon: ReactNode; title: MessageKey; body: MessageKey }[] = [
  { icon: <Zap size={18} />, title: 'home.trust.fast.title', body: 'home.trust.fast.body' },
  { icon: <Lock size={18} />, title: 'home.trust.private.title', body: 'home.trust.private.body' },
  { icon: <MessageCircle size={18} />, title: 'home.trust.whatsapp.title', body: 'home.trust.whatsapp.body' },
]

const STEPS: { icon: ReactNode; extraIcon: ReactNode; title: MessageKey; body: MessageKey; extra: MessageKey }[] = [
  {
    icon: <Camera size={20} />,
    extraIcon: <Crop size={18} />,
    title: 'home.step1.title',
    body: 'home.step1.body',
    extra: 'home.step1.extra',
  },
  {
    icon: <Wand2 size={20} />,
    extraIcon: <RectangleVertical size={18} />,
    title: 'home.step2.title',
    body: 'home.step2.body',
    extra: 'home.step2.extra',
  },
  {
    icon: <MessageCircle size={20} />,
    extraIcon: <CircleCheck size={18} />,
    title: 'home.step3.title',
    body: 'home.step3.body',
    extra: 'home.step3.extra',
  },
]

const SELLERS: MessageKey[] = ['home.sellers.1', 'home.sellers.2', 'home.sellers.3', 'home.sellers.4']
const BUYERS: MessageKey[] = ['home.buyers.1', 'home.buyers.2', 'home.buyers.3', 'home.buyers.4']
const FAQ: [MessageKey, MessageKey][] = [
  ['home.faq.q1', 'home.faq.a1'],
  ['home.faq.q2', 'home.faq.a2'],
  ['home.faq.q3', 'home.faq.a3'],
  ['home.faq.q4', 'home.faq.a4'],
]

/** Public landing page. Every claim here must stay true for the product as built. */
export function HomePage() {
  const { t, locale } = useI18n()
  usePageMeta({ title: t('home.title'), description: `${t('home.shoppers')} ${t('home.sellerIntro')}`, image: '/landing/tryon-result.webp' })
  return (
    <div className="landing">
      <section className="landing-hero">
        <div className="landing-hero-copy">
          <h1>{t('home.title')}</h1>
          <p className="lead">{t('home.subtitle')}</p>
          <div className="audience-actions">
            <div className="audience-action">
              <span className="eyebrow">{t('home.shopperLabel')}</span>
              <p>{t('home.shopperBrief')}</p>
              <Link to={SHOPPER_PATH} className="btn btn-primary">
                <PlayCircle size={18} aria-hidden="true" />{t('home.seeExample')}
              </Link>
            </div>
            <div className="audience-action">
              <span className="eyebrow">{t('home.sellerLabel')}</span>
              <p>{t('home.sellerBrief')}</p>
              <Link to="/studio" className="btn btn-secondary">
                {t('home.createShop')}<ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        <figure className="card hero-card">
          <div className="hero-split">
            <div className="hero-pane">
              <img src="/landing/hanger-jacket.webp" alt={t('home.hero.before')} width={640} height={857} />
              <span className="hero-chip hero-chip-light" aria-hidden="true">{t('home.hero.before')}</span>
            </div>
            <div className="hero-pane hero-pane-result">
              <img src="/landing/tryon-result.webp" alt={t('home.hero.alt')} fetchPriority="high" width={640} height={862} />
              <span className="hero-chip hero-chip-wa">
                <CircleCheck size={14} aria-hidden="true" />
                {t('home.hero.after')}
              </span>
            </div>
            <span className="hero-magic">
              <Sparkles size={14} aria-hidden="true" />
              {t('home.hero.magic')}
            </span>
          </div>
          <figcaption>
            <span className="hero-caption">
              <Zap size={16} aria-hidden="true" />
              {t('home.hero.caption')}
            </span>
            <span className="hint">{t('home.hero.credit')}</span>
          </figcaption>
        </figure>
      </section>

      <ul className="card trust-list">
        {TRUST.map((item) => (
          <li key={item.title}>
            <span className="icon-bubble" aria-hidden="true">
              {item.icon}
            </span>
            <div>
              <strong>{t(item.title)}</strong>
              <p>{t(item.body)}</p>
            </div>
          </li>
        ))}
      </ul>

      <section className="landing-section">
        <SectionHead icon={<Wand2 size={16} />} eyebrow={t('home.how.eyebrow')} title={t('home.how.title')}>
          {t('home.how.subtitle')}
        </SectionHead>
        <ol className="landing-steps">
          {STEPS.map((step, index) => (
            <li key={step.title} className="card step-card">
              <div className="step-top">
                <span className="step-chip">{t('home.step', { n: String(index + 1).padStart(2, '0') })}</span>
                <span className="icon-bubble" aria-hidden="true">
                  {step.icon}
                </span>
              </div>
              <h3>{t(step.title)}</h3>
              <p>{t(step.body)}</p>
              <p className="step-extra">
                <span aria-hidden="true">{step.extraIcon}</span>
                {t(step.extra)}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-section">
        <SectionHead title={t('home.sides.title')}>{t('home.sides.subtitle')}</SectionHead>
        <div className="sides">
          <Side
            tone="brand"
            icon={<Store size={16} />}
            title={t('home.sellers.title')}
            tag={t('home.sellers.tag')}
            items={SELLERS.map((key) => t(key))}
          />
          <Side
            tone="accent"
            icon={<ShoppingBag size={16} />}
            title={t('home.buyers.title')}
            tag={t('home.buyers.tag')}
            items={BUYERS.map((key) => t(key))}
          />
        </div>
      </section>

      <section className="landing-section landing-status">
        <SectionHead
          icon={<RectangleVertical size={16} />}
          eyebrow={t('home.status.eyebrow')}
          title={t('home.status.title')}
        >
          {t('home.status.subtitle')}
        </SectionHead>
        <div className="status-strip">
          {(locale === 'fr' ? ['fr', 'en'] : ['en', 'fr']).map((language) => (
            <img
              key={language}
              src={`/landing/status-${language}.webp`}
              alt={t('home.status.alt')}
              loading="lazy"
              width={540}
              height={960}
            />
          ))}
        </div>
      </section>

      <dl className="card facts">
        <Fact value="< 1 min" label={t('home.facts.time')} />
        <Fact value="0" label={t('home.facts.apps')} />
        <Fact value="24 h" label={t('home.facts.privacy')} />
        <Fact value="FR · EN" label={t('home.facts.languages')} />
      </dl>

      <section className="landing-section">
        <SectionHead icon={<CircleHelp size={16} />} eyebrow={t('home.faq.eyebrow')} title={t('home.faq.title')} />
        <div className="faq">
          {FAQ.map(([question, answer]) => (
            <details key={question} className="card">
              <summary>{t(question)}</summary>
              <p>{t(answer)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <span className="badge badge-on-dark">
          <Sparkles size={14} aria-hidden="true" />
          {t('home.cta.badge')}
        </span>
        <h2>{t('home.cta.title')}</h2>
        <p>{t('home.cta.body')}</p>
        <Link to="/studio" className="btn btn-light btn-lg">
          {t('home.createShop')}
          <ArrowRight size={18} aria-hidden="true" />
        </Link>
      </section>

      <footer className="landing-footer">
        <span className="brand">
          <LogoMark size={28} />
          <Wordmark />
        </span>
        <p>{t('home.footer.made')} 🇨🇲</p>
        <LanguageToggle />
        <LegalLinks />
        <p className="small">
          {t('home.footer.place')} · © 2026 TryOnMe
        </p>
      </footer>

      <div className="landing-sticky">
        <Link to="/studio" className="btn btn-primary btn-lg btn-block">
          <Store size={18} aria-hidden="true" />
          {t('home.createShop')}
        </Link>
      </div>
    </div>
  )
}

function SectionHead({
  icon,
  eyebrow,
  title,
  children,
}: {
  icon?: ReactNode
  eyebrow?: string
  title: string
  children?: ReactNode
}) {
  return (
    <div className="landing-head">
      {eyebrow !== undefined && (
        <p className="eyebrow">
          <span aria-hidden="true">{icon}</span>
          {eyebrow}
        </p>
      )}
      <h2>{title}</h2>
      {children !== undefined && <p className="muted">{children}</p>}
    </div>
  )
}

function Side({
  tone,
  icon,
  title,
  tag,
  items,
}: {
  tone: 'brand' | 'accent'
  icon: ReactNode
  title: string
  tag: string
  items: string[]
}) {
  return (
    <article className={`card side side-${tone}`}>
      <div className="side-head">
        <h3 className="side-title">
          <span aria-hidden="true">{icon}</span>
          {title}
        </h3>
        <span className="side-tag">{tag}</span>
      </div>
      <ul>
        {items.map((item) => (
          <li key={item}>
            <CircleCheck size={20} aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  )
}

function Fact({ value, label }: { value: string; label: string }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
